import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Pagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Cancel as DenyIcon,
  CheckCircle as ApproveIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DraftIcon,
  Edit as EditIcon,
  LocalHospital as LocalHospitalIcon,
  OpenInNew as OpenFormIcon,
  EmailOutlined as EmailIcon,
  ErrorOutline as DeniedIcon,
  MoreVert as MoreVertIcon,
  Paid as PaidIcon,
  Payments as PaymentsIcon,
  Print as PrintIcon,
  ReceiptLong as ReceiptIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Send as SubmitIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import StatTile from "../../components/StatTile";
import billingService from "../../services/billingService";
import residentService from "../../services/residentService";
import { InsuranceCard } from "../../types/resident.types";
import { useAuth } from "../../contexts/AuthContext";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid } from "date-fns";
import {
  BillingClaimDTO,
  BillingClaimSettlementDTO,
  BillingClaimSummary,
  ClaimFormType,
  ClaimSettlementSummary,
  ClaimStatus,
  PaymentMode,
  SettlementPayerType,
} from "../../types/billing.types";
import { formatToUIDate, formatToUIDateTime } from "../../utils/dateUtils";
import {
  ALL_CLAIM_FORMS,
  CLAIM_FORMS,
  CLAIM_STATUS_META,
  canApproveClaims,
  canPostSettlements,
  getApiErrorMessage,
  getClaimForm,
} from "./billingConfig";
import { downloadPdf, printClaimPdfDirect } from "./claimPdf";
import InvoicesListSection from "./InvoicesListSection";

const SEARCH_DEBOUNCE_MS = 300;

type TileFilter = "ALL" | "DRAFT" | "SUBMITTED" | "PAID" | "DENIED";

const TILE_STATUSES: Record<Exclude<TileFilter, "ALL">, ClaimStatus[]> = {
  DRAFT: [ClaimStatus.DRAFT],
  SUBMITTED: [ClaimStatus.SUBMITTED, ClaimStatus.IN_REVIEW],
  PAID: [ClaimStatus.APPROVED, ClaimStatus.PAID, ClaimStatus.PARTIAL_PAID],
  DENIED: [ClaimStatus.DENIED],
};

const PAYMENT_MODES = [
  { value: "CASH", label: "Cash" },
  { value: "BANK_TRANSFER", label: "Bank Transfer" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "CREDIT_CARD", label: "Credit Card" },
  { value: "DEBIT_CARD", label: "Debit Card" },
  { value: "INSURANCE", label: "Insurance" },
  { value: "OTHER", label: "Other" },
];

const PAYER_TYPES: { value: SettlementPayerType; label: string }[] = [
  { value: "PRIMARY_INSURANCE", label: "Primary Insurance" },
  { value: "SECONDARY_INSURANCE", label: "Secondary Insurance" },
  { value: "MEDICARE", label: "Medicare" },
  { value: "MEDICAID", label: "Medicaid" },
  { value: "PATIENT", label: "Patient / Self-Pay" },
  { value: "GUARANTOR", label: "Guarantor / Third Party" },
  { value: "OTHER", label: "Other" },
];

const formatCurrency = (amount?: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(amount) || 0);

// Service dates are date-only ("yyyy-MM-dd"); parse them as local dates so
// they don't shift a day in timezones behind UTC.
const formatServiceDate = (value?: string) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? formatToUIDate(new Date(+match[1], +match[2] - 1, +match[3])) : formatToUIDate(value);
};

const actionButtonSx = {
  borderRadius: "12px",
  px: 2.5,
  py: 1,
  fontWeight: "normal",
  textTransform: "none",
  boxShadow: "none",
  backgroundColor: "#48CAE4",
  color: "#000000",
  "&:hover": { backgroundColor: "#3ab3cc", boxShadow: "none" },
} as const;

// Feature flag: set to true to show the Individual Invoices tab and section
const SHOW_INVOICE_SECTION = false;

const BillingClaimsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<"claims" | "invoices">(
    SHOW_INVOICE_SECTION && tabParam === "invoices" ? "invoices" : "claims"
  );

  useEffect(() => {
    if (SHOW_INVOICE_SECTION && tabParam === "invoices" && activeTab !== "invoices") {
      setActiveTab("invoices");
    } else if ((!tabParam || !SHOW_INVOICE_SECTION) && activeTab !== "claims") {
      setActiveTab("claims");
    }
  }, [tabParam, activeTab]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: "claims" | "invoices") => {
    setActiveTab(newValue);
    if (newValue === "invoices") {
      setSearchParams({ tab: "invoices" });
    } else {
      setSearchParams({});
    }
  };

  const { user } = useAuth();
  const isApprover = canApproveClaims(user?.role);
  const canSettleClaims = canPostSettlements(user?.role);

  const [claims, setClaims] = useState<BillingClaimDTO[]>([]);
  const [totalMatching, setTotalMatching] = useState(0);
  const [summary, setSummary] = useState<BillingClaimSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [tileFilter, setTileFilter] = useState<TileFilter>("ALL");
  const [formFilter, setFormFilter] = useState<ClaimFormType | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const [newClaimAnchor, setNewClaimAnchor] = useState<HTMLElement | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [actionMenuClaim, setActionMenuClaim] = useState<BillingClaimDTO | null>(null);
  const [printingPdf, setPrintingPdf] = useState(false);
  const [detailClaim, setDetailClaim] = useState<BillingClaimDTO | null>(null);
  const [denyClaim, setDenyClaim] = useState<BillingClaimDTO | null>(null);
  const [denyReason, setDenyReason] = useState("");
  const [denyCode, setDenyCode] = useState("");
  const [deleteClaim, setDeleteClaim] = useState<BillingClaimDTO | null>(null);
  const [settlementClaim, setSettlementClaim] = useState<BillingClaimDTO | null>(null);
  const [settlementAmount, setSettlementAmount] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<PaymentMode>("BANK_TRANSFER");
  const [paymentDate, setPaymentDate] = useState<Date | null>(() => new Date());
  const [payerType, setPayerType] = useState<SettlementPayerType>("PRIMARY_INSURANCE");
  const [paymentReference, setPaymentReference] = useState<string>("");
  const [settlementRemarks, setSettlementRemarks] = useState<string>("");
  // Balance from the server's settlement ledger; the claim row may be stale
  const [settlementSummary, setSettlementSummary] = useState<ClaimSettlementSummary | null>(null);
  const [actionInProgress, setActionInProgress] = useState(false);

  // Drawer settlement receipts state
  const [detailSettlements, setDetailSettlements] = useState<BillingClaimSettlementDTO[]>([]);
  const [loadingSettlements, setLoadingSettlements] = useState<boolean>(false);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<string | null>(null);

  // Email dialog state
  const [emailClaim, setEmailClaim] = useState<BillingClaimDTO | null>(null);
  const [loadingInsurances, setLoadingInsurances] = useState(false);
  const [availableEmailOptions, setAvailableEmailOptions] = useState<{ email: string; label: string; isClaimPayer?: boolean }[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [emailInputValue, setEmailInputValue] = useState<string>("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" | "warning" | "info" }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await billingService.searchBillingClaims({
        status: tileFilter === "ALL" ? undefined : TILE_STATUSES[tileFilter],
        formType: formFilter === "ALL" ? undefined : formFilter,
        search: debouncedSearch || undefined,
        page,
        size: rowsPerPage,
      });
      setClaims(response.content || []);
      setTotalMatching(response.totalElements || 0);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, "Could not load billing claims."));
    } finally {
      setLoading(false);
    }
  }, [tileFilter, formFilter, debouncedSearch, page, rowsPerPage]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await billingService.getSummary());
    } catch {
      // Tiles fall back to "–"; the list itself reports load errors
      setSummary(null);
    }
  }, []);

  const refresh = useCallback(() => Promise.all([loadClaims(), loadSummary()]), [loadClaims, loadSummary]);

  const handleResetFilters = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
    setFormFilter("ALL");
    setTileFilter("ALL");
    setPage(0);
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // A new search starts from the first page
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  // Message passed back from the claim editor after a save
  useEffect(() => {
    const { message, severity } = (location.state as { message?: string; severity?: "success" | "warning" } | null) || {};
    if (message) {
      setSnackbar({ open: true, message, severity: severity || "success" });
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location, navigate]);

  const tileCount = (tile: TileFilter): number | undefined => {
    if (!summary) return undefined;
    if (tile === "ALL") return summary.totalClaims;
    return TILE_STATUSES[tile].reduce((sum, status) => sum + (summary.countsByStatus[status] || 0), 0);
  };

  const hasActiveFilters = tileFilter !== "ALL" || formFilter !== "ALL" || Boolean(debouncedSearch);

  const loadDetailSettlements = useCallback(async (claimId: string) => {
    setLoadingSettlements(true);
    try {
      const res = await billingService.listSettlements(claimId);
      const items = Array.isArray(res) ? res : (res?.content || []);
      // Sort chronologically so earliest installment is #1, then #2, etc.
      items.sort((a, b) => new Date(a.transactionDate || a.recordedAt || 0).getTime() - new Date(b.transactionDate || b.recordedAt || 0).getTime());
      setDetailSettlements(items);
    } catch (err) {
      console.error("Failed to load settlements:", err);
      setDetailSettlements([]);
    } finally {
      setLoadingSettlements(false);
    }
  }, []);

  useEffect(() => {
    if (detailClaim?.id) {
      loadDetailSettlements(detailClaim.id);
    } else {
      setDetailSettlements([]);
    }
  }, [detailClaim?.id, detailClaim?.paidAmount, detailClaim?.balance, loadDetailSettlements]);

  const handleDownloadReceipt = async (claimId: string, settlementId: string, receiptNumber?: string) => {
    try {
      setDownloadingReceiptId(settlementId);
      const blob = await billingService.getSettlementReceipt(claimId, settlementId);
      downloadPdf(blob, `receipt-${receiptNumber || settlementId}.pdf`);
      setSnackbar({
        open: true,
        message: `Receipt ${receiptNumber || ""} downloaded successfully.`,
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, "Could not download settlement receipt."),
        severity: "error",
      });
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const handleViewReceipt = async (claimId: string, settlementId: string) => {
    try {
      setDownloadingReceiptId(settlementId);
      const blob = await billingService.getSettlementReceipt(claimId, settlementId);
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      window.open(url, "_blank");
      window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, "Could not open settlement receipt."),
        severity: "error",
      });
    } finally {
      setDownloadingReceiptId(null);
    }
  };

  const runClaimAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
    failureMessage: string,
    keepDetailClaimId?: string
  ) => {
    setActionInProgress(true);
    try {
      await action();
      setSnackbar({ open: true, message: successMessage, severity: "success" });
      if (keepDetailClaimId) {
        try {
          const updated = await billingService.getBillingClaimById(keepDetailClaimId);
          setDetailClaim(updated);
          loadDetailSettlements(keepDetailClaimId);
        } catch {
          setDetailClaim(null);
        }
      } else {
        setDetailClaim(null);
      }
      await refresh();
    } catch (err) {
      setSnackbar({ open: true, message: getApiErrorMessage(err, failureMessage), severity: "error" });
    } finally {
      setActionInProgress(false);
    }
  };

  const handleSubmitClaim = (claim: BillingClaimDTO) =>
    runClaimAction(() => billingService.submitBillingClaim(claim.id!), `Claim ${claim.claimNumber} submitted.`, "Could not submit the claim.");

  const openSettlementDialog = (claim: BillingClaimDTO) => {
    const balance = getClaimDueBalance(claim);
    setSettlementClaim(claim);
    setSettlementSummary(null);
    setSettlementAmount(balance > 0 ? balance.toFixed(2) : "");
    setPaymentMode("BANK_TRANSFER");
    setPaymentDate(new Date());
    setPayerType(claim.payerName ? "PRIMARY_INSURANCE" : "PATIENT");
    setPaymentReference("");
    setSettlementRemarks("");
    billingService
      .getSettlementSummary(claim.id!)
      .then((summary) => {
        setSettlementSummary(summary);
        setSettlementAmount(summary.balanceDue > 0 ? Number(summary.balanceDue).toFixed(2) : "");
      })
      .catch((err) =>
        setSnackbar({ open: true, message: getApiErrorMessage(err, "Could not load the claim balance."), severity: "error" })
      );
  };

  const handleConfirmSettlement = async () => {
    if (!settlementClaim) return;
    const claim = settlementClaim;
    const isFromDrawer = detailClaim?.id === claim.id;
    const amount = Math.round(parseFloat(settlementAmount) * 100) / 100;
    if (!amount || amount <= 0) return;
    const transactionDate = paymentDate && isValid(paymentDate) ? format(paymentDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");

    setSettlementClaim(null);
    await runClaimAction(
      async () => {
        // The server validates the amount against the ledger and computes every total
        const settlement = await billingService.recordSettlement(claim.id!, {
          amount,
          paymentMode,
          transactionDate,
          payerType,
          paymentReference: paymentReference.trim() || undefined,
          remarks: settlementRemarks.trim() || undefined,
        });
        try {
          const receipt = await billingService.getSettlementReceipt(claim.id!, settlement.id);
          downloadPdf(receipt, `receipt-${settlement.receiptNumber}.pdf`);
        } catch {
          // The payment is posted; the receipt can be downloaded again later
        }
        return settlement;
      },
      `Settlement of $${amount.toFixed(2)} posted for claim ${claim.claimNumber}.`,
      "Could not complete bill settlement.",
      isFromDrawer ? claim.id : undefined
    );
  };

  const handlePrintClaim = (claim: BillingClaimDTO) => {
    if (!claim.id) return;
    printClaimPdfDirect(claim.id).catch((err) =>
      setSnackbar({ open: true, message: getApiErrorMessage(err, "Could not generate the claim form PDF."), severity: "error" })
    );
  };

  const handleConfirmDeny = async () => {
    if (!denyClaim) return;
    const claim = denyClaim;
    setDenyClaim(null);
    await runClaimAction(
      () => billingService.rejectBillingClaim(claim.id!, denyReason.trim(), denyCode.trim() || undefined),
      `Claim ${claim.claimNumber} rejected.`,
      "Could not reject the claim."
    );
  };

  const handleConfirmDelete = async () => {
    if (!deleteClaim) return;
    const claim = deleteClaim;
    setDeleteClaim(null);
    await runClaimAction(() => billingService.deleteBillingClaim(claim.id!), `Claim ${claim.claimNumber} deleted.`, "Could not delete the claim.");
  };

  const openDenyDialog = (claim: BillingClaimDTO) => {
    setDenyReason("");
    setDenyCode("");
    setDenyClaim(claim);
  };

  const handleOpenActionMenu = (event: React.MouseEvent<HTMLElement>, claim: BillingClaimDTO) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuClaim(claim);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuClaim(null);
  };

  const handleOpenEmailDialog = async (claim: BillingClaimDTO) => {
    setEmailClaim(claim);
    setAvailableEmailOptions([]);
    setSelectedEmails([]);
    setEmailInputValue("");
    setLoadingInsurances(true);

    try {
      const [cards, resident] = await Promise.all([
        claim.residentId ? residentService.getResidentInsurances(claim.residentId) : Promise.resolve([]),
        claim.residentId ? residentService.getResidentById(claim.residentId).catch(() => null) : Promise.resolve(null),
      ]);
      const validCards = Array.isArray(cards) ? cards : [];
      const optionsMap = new Map<string, { email: string; label: string; isClaimPayer?: boolean }>();

      validCards.forEach((c, idx) => {
        const rawEmail = c.insuranceEmail?.trim();
        if (rawEmail) {
          const isClaimPayer = Boolean(
            claim.payerName &&
            c.insuranceCompanyName &&
            c.insuranceCompanyName.trim().toLowerCase() === claim.payerName.trim().toLowerCase()
          );
          const pName = c.insuranceCompanyName || c.provider || `Insurance #${idx + 1}`;
          const type = c.coverageType || c.insuranceType;
          const label = isClaimPayer
            ? `${pName}${type ? ` (${type})` : ""} • Claimed Payer`
            : `${pName}${type ? ` (${type})` : ""}`;
          optionsMap.set(rawEmail.toLowerCase(), {
            email: rawEmail,
            label,
            isClaimPayer,
          });
        }
      });

      if (resident?.email && resident.email.trim()) {
        const resEmail = resident.email.trim();
        if (!optionsMap.has(resEmail.toLowerCase())) {
          const resName = [resident.firstName, resident.lastName].filter(Boolean).join(" ") || "Resident";
          optionsMap.set(resEmail.toLowerCase(), {
            email: resEmail,
            label: `${resName} (Resident Personal Email)`,
            isClaimPayer: false,
          });
        }
      }

      const allOptions = Array.from(optionsMap.values());
      setAvailableEmailOptions(allOptions);

      // Pre-select the claimed payer's email by default if available
      const claimPayerOption = allOptions.find((opt) => opt.isClaimPayer);
      if (claimPayerOption) {
        setSelectedEmails([claimPayerOption.email]);
      } else if (allOptions.length > 0) {
        setSelectedEmails([allOptions[0].email]);
      } else {
        setSelectedEmails([]);
      }
    } catch (err) {
      console.warn("Could not load resident insurances for email dialog:", err);
      setAvailableEmailOptions([]);
      setSelectedEmails([]);
    } finally {
      setLoadingInsurances(false);
    }
  };

  const handleCloseEmailDialog = () => {
    if (sendingEmail) return;
    setEmailClaim(null);
    setAvailableEmailOptions([]);
    setSelectedEmails([]);
    setEmailInputValue("");
  };

  const handleSendEmail = async () => {
    const pendingInput = emailInputValue.trim();
    let toSend = [...selectedEmails];
    if (pendingInput && !toSend.includes(pendingInput)) {
      toSend.push(pendingInput);
      setSelectedEmails(toSend);
      setEmailInputValue("");
    }
    if (!emailClaim || toSend.length === 0) return;
    setSendingEmail(true);
    try {
      await billingService.sendClaimEmail(emailClaim.id!, toSend);
      setSnackbar({
        open: true,
        message: "Claim emailed successfully!",
        severity: "success",
      });
      handleCloseEmailDialog();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, "Failed to send claim email."),
        severity: "error",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  // Claims saved before form data was stored have no form type to reopen
  const hasClaimForm = (claim: BillingClaimDTO) => Boolean(getClaimForm(claim.formType));

  const claimFormPath = (claim: BillingClaimDTO) =>
    claim.editable ? `/billing/${claim.id}/edit` : `/billing/${claim.id}`;

  const canSubmit = (claim: BillingClaimDTO) => claim.status === ClaimStatus.DRAFT;
  const canDelete = (claim: BillingClaimDTO) => isApprover && claim.status === ClaimStatus.DRAFT;
  const canEmailOrPrint = (claim?: BillingClaimDTO | null): boolean => {
    if (!claim || !claim.status) return false;
    return (
      claim.status === ClaimStatus.SUBMITTED ||
      claim.status === ClaimStatus.APPROVED ||
      claim.status === ClaimStatus.PAID ||
      claim.status === ClaimStatus.PARTIAL_PAID
    );
  };

  const getClaimDueBalance = (claim: BillingClaimDTO): number => {
    if (claim.balance != null) return Math.max(0, claim.balance);
    return Math.max(0, (claim.billedAmount || 0) - (claim.paidAmount || 0));
  };

  const hasDueBalance = (claim: BillingClaimDTO): boolean => {
    return getClaimDueBalance(claim) > 0.009;
  };

  const canReject = (claim: BillingClaimDTO) =>
    isApprover && (claim.status === ClaimStatus.SUBMITTED || claim.status === ClaimStatus.IN_REVIEW);

  const canSettle = (claim: BillingClaimDTO) => {
    if (!canSettleClaims) return false;
    // Payments are accepted while the claim is with the payer or approved, as long as money is due
    const acceptsPayments =
      claim.status === ClaimStatus.SUBMITTED ||
      claim.status === ClaimStatus.IN_REVIEW ||
      claim.status === ClaimStatus.APPROVED ||
      claim.status === ClaimStatus.PARTIAL_PAID;
    return acceptsPayments && hasDueBalance(claim);
  };

  const renderStatusChip = (status?: ClaimStatus) => {
    const meta = status ? CLAIM_STATUS_META[status] : undefined;
    const isApproved = status === ClaimStatus.APPROVED;
    return (
      <Chip
        size="small"
        label={meta?.label || status || "Unknown"}
        color={meta?.color || "default"}
        sx={{
          fontWeight: 600,
          ...(isApproved && {
            bgcolor: "#DCFCE7",
            color: "#16A34A",
          }),
        }}
      />
    );
  };

  const renderFormChip = (claim: BillingClaimDTO) => {
    const form = getClaimForm(claim.formType);
    return form ? <Chip size="small" variant="outlined" label={form.name} /> : <Typography variant="body2">—</Typography>;
  };

  const tiles: { key: TileFilter; label: string; color: string; icon: React.ReactElement }[] = [
    { key: "ALL", label: "All Claims", color: "#00B4D8", icon: <ReceiptIcon /> },
    { key: "DRAFT", label: "Drafts", color: "#64748B", icon: <DraftIcon /> },
    { key: "SUBMITTED", label: "Submitted", color: "#60a5fa", icon: <SubmitIcon /> },
    { key: "PAID", label: "Paid", color: "#34d399", icon: <PaidIcon /> },
    { key: "DENIED", label: "Rejected", color: "#f87171", icon: <DeniedIcon /> },
  ];

  const detailRows: [string, React.ReactNode][] = detailClaim
    ? [
      ["Claim Form", getClaimForm(detailClaim.formType)?.name || "—"],
      ["Status", renderStatusChip(detailClaim.status)],
      ["Resident", detailClaim.residentName || "—"],
      ["Payer", detailClaim.payerName],
      ["Claim Type", detailClaim.claimType?.replace(/_/g, " ")],
      ["Policy / Member ID", detailClaim.policyNumber || "—"],
      ["Service Dates", `${formatServiceDate(detailClaim.serviceDateFrom)} – ${formatServiceDate(detailClaim.serviceDateTo)}`],
      ["Diagnosis Codes", detailClaim.diagnosisCodes],
      ["Procedure Codes", detailClaim.procedureCodes],
      ["Total Billed", formatCurrency(detailClaim.billedAmount)],
      ["Paid", formatCurrency(detailClaim.paidAmount)],
      ["Balance", formatCurrency(detailClaim.balance)],
      ["Billing Provider NPI", detailClaim.billingProviderNpi || "—"],
      ["Submitted", detailClaim.submissionDate ? formatToUIDateTime(detailClaim.submissionDate) : "—"],
      ...(detailClaim.status === ClaimStatus.DENIED
        ? ([["Rejection Reason", [detailClaim.denialCode, detailClaim.denialReason].filter(Boolean).join(" – ") || "—"]] as [string, React.ReactNode][])
        : []),
      ["Created", detailClaim.createdAt ? formatToUIDateTime(detailClaim.createdAt) : "—"],
    ]
    : [];

  const currentBalanceDue = settlementSummary
    ? Math.max(0, Number(settlementSummary.balanceDue))
    : settlementClaim
      ? getClaimDueBalance(settlementClaim)
      : 0;
  const parsedSettlementAmount = parseFloat(settlementAmount);
  const isAmountExceedingBalance =
    currentBalanceDue > 0 && !isNaN(parsedSettlementAmount) && parsedSettlementAmount > currentBalanceDue + 0.009;

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ReceiptIcon sx={{ fontSize: 36, color: "#48CAE4" }} />
          <Box>
            <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5, color: "text.primary" }}>
              Billing & Claims
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.85rem" }}>
              {SHOW_INVOICE_SECTION && activeTab === "invoices"
                ? "Manage and track individual resident caregiving, private pay, and wrap around service invoices"
                : "Create, submit and track professional and institutional claims"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          {SHOW_INVOICE_SECTION && activeTab === "invoices" ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate("/billing/new/INVOICE")}
              sx={actionButtonSx}
            >
              New Invoice
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={(e) => setNewClaimAnchor(e.currentTarget)}
              sx={actionButtonSx}
            >
              New Claim
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs navigation */}
      {SHOW_INVOICE_SECTION && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2.5 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                minHeight: 44,
                py: 1,
                px: 2,
                gap: 1,
                color: "#64748B",
                "&.Mui-selected": {
                  color: "#0284C7",
                  fontWeight: 700,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#0284C7",
                height: 3,
              },
            }}
          >
            <Tab
              value="claims"
              icon={<LocalHospitalIcon fontSize="small" />}
              iconPosition="start"
              label="Insurance Claims (CMS-1500 / UB-04)"
            />
            <Tab
              value="invoices"
              icon={<ReceiptIcon fontSize="small" />}
              iconPosition="start"
              label="Individual Invoices"
            />
          </Tabs>
        </Box>
      )}

      <Menu
        anchorEl={newClaimAnchor}
        open={Boolean(newClaimAnchor)}
        onClose={() => setNewClaimAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 220, mt: 1, borderRadius: "10px" } }}
      >
        {(SHOW_INVOICE_SECTION ? ALL_CLAIM_FORMS : CLAIM_FORMS).map((form) => (
          <MenuItem
            key={form.id}
            onClick={() => {
              setNewClaimAnchor(null);
              navigate(`/billing/new/${form.id}`);
            }}
            sx={{ py: 1.25, px: 2 }}
          >
            <ListItemIcon sx={{ color: "primary.main", minWidth: 36 }}>
              <form.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={form.name}
              primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }}
            />
          </MenuItem>
        ))}
      </Menu>

      {SHOW_INVOICE_SECTION && activeTab === "invoices" ? (
        <InvoicesListSection
          onShowSnackbar={(message, severity) => setSnackbar({ open: true, message, severity })}
        />
      ) : (
        <>

          {/* Summary tiles */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {tiles.map((tile, index) => (
              <Grid item xs={index === 0 ? 12 : 6} sm={index === 0 ? 12 : 3} lg={2.4} key={tile.key}>
                <StatTile
                  label={tile.label}
                  value={tileCount(tile.key) ?? "–"}
                  icon={tile.icon}
                  color={tile.color}
                  active={tileFilter === tile.key}
                  onClick={() => {
                    setTileFilter(tile.key);
                    setPage(0);
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Filters */}
          <Paper elevation={0} sx={{ p: 2, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: "16px" }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by Claim, Resident, Payer"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 240 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                size="small"
                label="Claim Form"
                value={formFilter}
                onChange={(e) => {
                  setFormFilter(e.target.value as ClaimFormType | "ALL");
                  setPage(0);
                }}
                sx={{ width: 200 }}
              >
                <MenuItem value="ALL">All forms</MenuItem>
                {CLAIM_FORMS.map((form) => (
                  <MenuItem key={form.id} value={form.id}>
                    {form.name}
                  </MenuItem>
                ))}
              </TextField>
              <Tooltip title="Reset & Refresh Claims">
                <span>
                  <IconButton
                    onClick={handleResetFilters}
                    disabled={loading}
                    aria-label="Reset & refresh claims"
                    sx={{
                      color: loading ? "#94A3B8" : "#0284C7",
                      "&:hover": { backgroundColor: "rgba(2, 132, 199, 0.08)" },
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Paper>

          {loadError && (
            <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={loadClaims}>Retry</Button>}>
              {loadError}
            </Alert>
          )}

          {/* Claims table */}
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "16px" }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Claim</TableCell>
                  <TableCell>Resident</TableCell>
                  <TableCell>Form</TableCell>
                  <TableCell>Payer</TableCell>
                  <TableCell>Service Dates</TableCell>
                  <TableCell align="right">Total Billed</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: 92 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={32} />
                    </TableCell>
                  </TableRow>
                ) : claims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <ReceiptIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                      <Typography variant="subtitle1">
                        {hasActiveFilters ? "No claims match these filters" : "No claims yet"}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {hasActiveFilters
                          ? "Try a different status, form or search term."
                          : SHOW_INVOICE_SECTION
                          ? "Create a CMS-1500, UB-04 claim or Individual Invoice to get started."
                          : "Create a CMS-1500 or UB-04 claim to get started."}
                      </Typography>
                      {!hasActiveFilters && (
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={(e) => setNewClaimAnchor(e.currentTarget)} sx={actionButtonSx}>
                          New Claim
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  claims.map((claim) => (
                    <TableRow key={claim.id} hover sx={{ cursor: "pointer" }} onClick={() => setDetailClaim(claim)}>
                      <TableCell sx={{ fontWeight: 600 }}>{claim.claimNumber}</TableCell>
                      <TableCell>{claim.residentName || "—"}</TableCell>
                      <TableCell>{renderFormChip(claim)}</TableCell>
                      <TableCell>{claim.payerName}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatServiceDate(claim.serviceDateFrom)} – {formatServiceDate(claim.serviceDateTo)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(claim.billedAmount)}</TableCell>
                      <TableCell>{renderStatusChip(claim.status)}</TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                        <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          {/* Email Icon before 3-dots: preserves space when hidden so 3-dots stay perfectly aligned in one line */}
                          <Tooltip title={canEmailOrPrint(claim) ? "Email Claim" : ""}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEmailDialog(claim)}
                                aria-label="Email Claim"
                                tabIndex={canEmailOrPrint(claim) ? 0 : -1}
                                sx={{
                                  color: "#48CAE4",
                                  visibility: canEmailOrPrint(claim) ? "visible" : "hidden",
                                  pointerEvents: canEmailOrPrint(claim) ? "auto" : "none",
                                  "&:hover": {
                                    backgroundColor: "rgba(72, 202, 228, 0.12)",
                                  },
                                }}
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* 3-dots Menu Button */}
                          <Tooltip title="More Actions">
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenActionMenu(e, claim)}
                              aria-label="Actions"
                              sx={{
                                color: "#64748B",
                                "&:hover": {
                                  backgroundColor: "#F1F5F9",
                                  color: "#0F172A",
                                },
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Container */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 },
              mt: 2.5,
              pt: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {claims.length > 0 ? page * rowsPerPage + 1 : 0} to {Math.min((page + 1) * rowsPerPage, totalMatching)} of {totalMatching} entries
            </Typography>

            <Pagination
              count={Math.max(1, Math.ceil(totalMatching / rowsPerPage))}
              page={page + 1}
              onChange={(_, value) => setPage(value - 1)}
              variant="outlined"
              shape="rounded"
              size="small"
            />
          </Box>
        </>
      )}

      {/* Claim details */}
      <Drawer anchor="right" open={Boolean(detailClaim)} onClose={() => setDetailClaim(null)} PaperProps={{ sx: { width: { xs: "100%", sm: 460 } } }}>
        {detailClaim && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography variant="caption">Claim</Typography>
                <Typography variant="h5" fontWeight="700">{detailClaim.claimNumber}</Typography>
              </Box>
              <IconButton onClick={() => setDetailClaim(null)} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 3, flex: 1, overflowY: "auto" }}>
              {detailRows.map(([label, value]) => (
                <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Typography variant="body2">{label}</Typography>
                  <Box sx={{ textAlign: "right", fontSize: "0.875rem", fontWeight: 600, color: "text.primary", wordBreak: "break-word" }}>{value}</Box>
                </Box>
              ))}

              {/* ── Settlement Receipts (Installments) Section ── */}
              <Box sx={{ mt: 3, pt: 2.5, borderTop: "2px dashed", borderColor: "divider" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ReceiptIcon sx={{ color: "#0284C7", fontSize: 22 }} />
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: "text.primary", fontSize: "0.95rem" }}>
                      Settlement Receipts
                    </Typography>
                    {detailSettlements.length > 0 && (
                      <Chip
                        label={`${detailSettlements.length} ${detailSettlements.length === 1 ? "Receipt" : "Receipts"}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          backgroundColor: "#ECFDF5",
                          color: "#059669",
                          border: "1px solid #A7F3D0",
                        }}
                      />
                    )}
                  </Box>
                  {loadingSettlements && <CircularProgress size={16} sx={{ color: "#0284C7" }} />}
                </Box>

                {loadingSettlements && detailSettlements.length === 0 ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 3, gap: 1.5 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading receipts...</Typography>
                  </Box>
                ) : detailSettlements.length === 0 ? (
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#F8FAFC",
                      border: "1px dashed",
                      borderColor: "divider",
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.85rem" }}>
                      No settlement receipts generated yet.
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 0.5 }}>
                      Receipts will appear here automatically when bill settlements are recorded.
                    </Typography>
                  </Box>
                ) : (
                  detailSettlements.map((settlement, index) => (
                    <Paper
                      key={settlement.id || index}
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 1.5,
                        borderRadius: "12px",
                        borderColor: "#E2E8F0",
                        backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "#FFFFFF",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "#0284C7",
                          boxShadow: "0 4px 12px rgba(2, 132, 199, 0.08)",
                        },
                      }}
                    >
                      {/* Top row: Installment badge & Receipt Number & Status */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            label={`Installment #${index + 1}`}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.72rem",
                              backgroundColor: "#EFF6FF",
                              color: "#1D4ED8",
                              border: "1px solid #BFDBFE",
                              height: 22,
                            }}
                          />
                          <Typography variant="body2" fontWeight={700} sx={{ color: "text.primary", fontFamily: "monospace", letterSpacing: 0.5 }}>
                            {settlement.receiptNumber || `REC-${index + 1}`}
                          </Typography>
                        </Box>
                        <Chip
                          label={settlement.status === "POSTED" ? "Paid" : settlement.status}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            backgroundColor: settlement.status === "POSTED" ? "#DCFCE7" : "#F1F5F9",
                            color: settlement.status === "POSTED" ? "#15803D" : "#64748B",
                          }}
                        />
                      </Box>

                      {/* Amount & Date row */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1.25, pb: 1, borderBottom: "1px dashed", borderColor: "divider" }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.75rem" }}>
                            Amount Paid
                          </Typography>
                          <Typography variant="h6" fontWeight={700} sx={{ color: "#16A34A", fontSize: "1.15rem", lineHeight: 1.2 }}>
                            ${Number(settlement.amount || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.75rem" }}>
                            Date
                          </Typography>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "text.primary" }}>
                            {settlement.transactionDate ? formatToUIDate(settlement.transactionDate) : "-"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Additional metadata */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, fontSize: "0.8125rem", color: "text.secondary" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <span>Payment Mode:</span>
                          <strong style={{ color: "var(--mui-palette-text-primary, #334155)" }}>
                            {settlement.paymentMode ? settlement.paymentMode.replace(/_/g, " ") : "Direct"}
                          </strong>
                        </Box>
                        {(settlement.payerType || settlement.payerName) && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Payer:</span>
                            <strong style={{ color: "var(--mui-palette-text-primary, #334155)" }}>
                              {settlement.payerName || settlement.payerType?.replace(/_/g, " ")}
                            </strong>
                          </Box>
                        )}
                        {settlement.paymentReference && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Reference / Check #:</span>
                            <strong style={{ color: "var(--mui-palette-text-primary, #334155)" }}>
                              {settlement.paymentReference}
                            </strong>
                          </Box>
                        )}
                        {settlement.balanceAfter !== undefined && settlement.balanceAfter !== null && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Balance After:</span>
                            <strong style={{ color: settlement.balanceAfter === 0 ? "#16A34A" : "#D97706" }}>
                              ${Number(settlement.balanceAfter).toFixed(2)}
                            </strong>
                          </Box>
                        )}
                        {settlement.remarks && (
                          <Box sx={{ mt: 0.5, p: 0.75, borderRadius: 1, backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#F1F5F9", fontStyle: "italic", fontSize: "0.75rem" }}>
                            Note: {settlement.remarks}
                          </Box>
                        )}
                      </Box>

                      {/* Actions: Download Receipt button */}
                      <Box sx={{ mt: 1.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          startIcon={downloadingReceiptId === settlement.id ? <CircularProgress size={14} color="inherit" /> : <ReceiptIcon />}
                          disabled={downloadingReceiptId === settlement.id}
                          onClick={() => handleDownloadReceipt(detailClaim.id!, settlement.id, settlement.receiptNumber)}
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                            backgroundColor: "#0284C7",
                            boxShadow: "none",
                            "&:hover": { backgroundColor: "#0369A1", boxShadow: "none" },
                          }}
                        >
                          {downloadingReceiptId === settlement.id ? "Downloading..." : "Download Receipt"}
                        </Button>
                      </Box>
                    </Paper>
                  ))
                )}
              </Box>
            </Box>
            <Divider />
            <Box sx={{ p: 2, display: "flex", gap: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
              {hasClaimForm(detailClaim) && (
                <Button
                  variant="outlined"
                  startIcon={detailClaim.editable ? <EditIcon /> : <OpenFormIcon />}
                  onClick={() => navigate(claimFormPath(detailClaim))}
                  sx={{ mr: "auto" }}
                >
                  {detailClaim.editable ? "Edit Draft" : "Open Form"}
                </Button>
              )}
              {canEmailOrPrint(detailClaim) && (
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  disabled={printingPdf}
                  onClick={() => handlePrintClaim(detailClaim)}
                >
                  Print PDF
                </Button>
              )}
              {canReject(detailClaim) && (
                <Button variant="outlined" color="error" startIcon={<DenyIcon />} disabled={actionInProgress} onClick={() => openDenyDialog(detailClaim)}>
                  Reject Bill
                </Button>
              )}
              {canSettle(detailClaim) && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PaymentsIcon />}
                  disabled={actionInProgress}
                  onClick={() => openSettlementDialog(detailClaim)}
                  sx={{
                    ...actionButtonSx,
                    "&.Mui-disabled": {
                      bgcolor: "#E2E8F0",
                      color: "#94A3B8",
                    },
                  }}
                >
                  Bill Settlement
                </Button>
              )}
              {canSubmit(detailClaim) && (
                <Button variant="contained" startIcon={<SubmitIcon />} disabled={actionInProgress} onClick={() => handleSubmitClaim(detailClaim)}>
                  Submit Claim
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Reject dialog */}
      <Dialog open={Boolean(denyClaim)} onClose={() => setDenyClaim(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reject Bill {denyClaim?.claimNumber}</DialogTitle>
        <DialogContent sx={{ "& .MuiFormLabel-asterisk": { color: "#DC2626 !important" } }}>
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={2}
            label="Rejection reason"
            value={denyReason}
            onChange={(e) => setDenyReason(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField fullWidth label="Rejection code (optional)" value={denyCode} onChange={(e) => setDenyCode(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyClaim(null)}>Cancel</Button>
          <Button variant="contained" color="error" disabled={!denyReason.trim()} onClick={handleConfirmDeny}>
            Reject Bill
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bill Settlement Dialog */}
      <Dialog
        open={Boolean(settlementClaim)}
        onClose={() => setSettlementClaim(null)}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            pt: { xs: 5, sm: 15 },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "10px",
                  bgcolor: "rgba(22, 163, 74, 0.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#16A34A",
                }}
              >
                <PaymentsIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Bill Settlement
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Claim #{settlementClaim?.claimNumber} • {settlementClaim?.residentName || "Resident"}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setSettlementClaim(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            pt: 2,
            pb: 2,
            "& .MuiFormLabel-asterisk": {
              color: "#DC2626 !important",
            },
          }}
        >
          {/* Bill Summary Banner */}
          <Box
            sx={{
              p: 2,
              mb: 3,
              borderRadius: "12px",
              bgcolor: "rgba(2, 132, 199, 0.05)",
              border: "1px solid",
              borderColor: "rgba(2, 132, 199, 0.2)",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, display: "block" }}>
                  Total Billed
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
                  ${Number(settlementSummary?.totalBilled ?? settlementClaim?.billedAmount ?? 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, display: "block" }}>
                  Previously Paid
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary" }}>
                  ${Number(settlementSummary?.totalPaid ?? settlementClaim?.paidAmount ?? 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, display: "block" }}>
                  Balance Due
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#16A34A" }}>
                  ${currentBalanceDue.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
            {settlementClaim?.payerName && (
              <Box
                sx={{
                  mt: 1.2,
                  pt: 1.2,
                  borderTop: "1px dashed rgba(2, 132, 199, 0.2)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Payer: <strong>{settlementClaim.payerName}</strong>
                </Typography>
                {settlementClaim.policyNumber && (
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Policy #: <strong>{settlementClaim.policyNumber}</strong>
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Transaction Input Fields */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>
            Transaction Details
          </Typography>

          <Grid container spacing={2}>
            {/* Settlement Amount */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Settlement Amount"
                value={settlementAmount}
                onChange={(e) => setSettlementAmount(e.target.value)}
                error={isAmountExceedingBalance}
                helperText={
                  isAmountExceedingBalance
                    ? `Cannot exceed balance due ($${currentBalanceDue.toFixed(2)})`
                    : ""
                }
                inputProps={{ min: 0, step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            {/* Payment Mode */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
                <Select
                  labelId="payment-mode-label"
                  label="Payment Mode"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value as PaymentMode)}
                >
                  {PAYMENT_MODES.map((mode) => (
                    <MenuItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Transaction Date */}
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Transaction Date"
                  value={paymentDate}
                  onChange={(newDate: Date | null) => setPaymentDate(newDate)}
                  format="dd/MM/yyyy"
                  disableFuture
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                    popper: {
                      placement: "bottom-start",
                      modifiers: [
                        {
                          name: "flip",
                          enabled: false,
                        },
                        {
                          name: "preventOverflow",
                          enabled: true,
                          options: {
                            boundary: "viewport",
                            padding: 8,
                          },
                        },
                      ],
                      sx: {
                        zIndex: 1400,
                        "& .MuiDateCalendar-root": {
                          height: "auto",
                          maxHeight: 280,
                          width: 280,
                        },
                        "& .MuiDayCalendar-weekContainer": {
                          margin: "1px 0",
                        },
                        "& .MuiPickersDay-root": {
                          width: 32,
                          height: 32,
                          fontSize: "0.8rem",
                        },
                        "& .MuiDayCalendar-header": {
                          "& .MuiDayCalendar-weekDayLabel": {
                            width: 32,
                            height: 32,
                            fontSize: "0.75rem",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Paid By / Payer Type */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="payer-type-label">Paid By / Payer</InputLabel>
                <Select
                  labelId="payer-type-label"
                  label="Paid By / Payer"
                  value={payerType}
                  onChange={(e) => setPayerType(e.target.value as SettlementPayerType)}
                >
                  {PAYER_TYPES.map((pt) => (
                    <MenuItem key={pt.value} value={pt.value}>
                      {pt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Payment reference */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Reference"
                placeholder="Cheque number, EFT trace number or card authorization"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>

            {/* Remarks */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Transaction Remarks"
                placeholder="Enter transaction remarks, settlement details, or payer remittance info..."
                value={settlementRemarks}
                onChange={(e) => setSettlementRemarks(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSettlementClaim(null)} color="inherit" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<PaymentsIcon />}
            disabled={actionInProgress || !settlementAmount || parseFloat(settlementAmount) <= 0 || isAmountExceedingBalance}
            onClick={handleConfirmSettlement}
            sx={{
              ...actionButtonSx,
              "&.Mui-disabled": {
                bgcolor: "#E2E8F0",
                color: "#94A3B8",
              },
            }}
          >
            Confirm Settlement
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3-dot Row Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor && actionMenuClaim)}
        onClose={handleCloseActionMenu}
        onClick={(e) => e.stopPropagation()}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 4,
          sx: {
            minWidth: 190,
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            border: "1px solid",
            borderColor: "divider",
            py: 0.5,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionMenuClaim) setDetailClaim(actionMenuClaim);
            handleCloseActionMenu();
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ViewIcon fontSize="small" sx={{ color: "#0284C7" }} />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}>
            View Details
          </ListItemText>
        </MenuItem>

        {actionMenuClaim && hasClaimForm(actionMenuClaim) && (
          <MenuItem
            onClick={() => {
              const path = claimFormPath(actionMenuClaim);
              handleCloseActionMenu();
              navigate(path);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              {actionMenuClaim.editable ? (
                <EditIcon fontSize="small" sx={{ color: "#0284C7" }} />
              ) : (
                <OpenFormIcon fontSize="small" sx={{ color: "#0284C7" }} />
              )}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}>
              {actionMenuClaim.editable ? "Edit Draft" : "Open Form"}
            </ListItemText>
          </MenuItem>
        )}

        {actionMenuClaim && canEmailOrPrint(actionMenuClaim) && (
          <MenuItem
            disabled={printingPdf}
            onClick={() => {
              if (actionMenuClaim) handlePrintClaim(actionMenuClaim);
              handleCloseActionMenu();
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <PrintIcon fontSize="small" sx={{ color: "#475569" }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}>
              Print PDF
            </ListItemText>
          </MenuItem>
        )}

        {actionMenuClaim && canEmailOrPrint(actionMenuClaim) && (
          <MenuItem
            onClick={() => {
              const c = actionMenuClaim;
              handleCloseActionMenu();
              if (c) handleOpenEmailDialog(c);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <EmailIcon fontSize="small" sx={{ color: "#48CAE4" }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}>
              Email Claim
            </ListItemText>
          </MenuItem>
        )}

        {actionMenuClaim && canSubmit(actionMenuClaim) && (
          <MenuItem
            disabled={actionInProgress}
            onClick={() => {
              const c = actionMenuClaim;
              handleCloseActionMenu();
              handleSubmitClaim(c);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <SubmitIcon fontSize="small" sx={{ color: "#0284C7" }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}>
              Submit Claim
            </ListItemText>
          </MenuItem>
        )}

        {actionMenuClaim && canSettle(actionMenuClaim) && (
          <MenuItem
            disabled={actionInProgress}
            onClick={() => {
              const c = actionMenuClaim;
              handleCloseActionMenu();
              openSettlementDialog(c);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <PaymentsIcon fontSize="small" sx={{ color: "#16A34A" }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500, color: "#16A34A" }}
            >
              Bill Settlement
            </ListItemText>
          </MenuItem>
        )}

        {actionMenuClaim && canReject(actionMenuClaim) && (
          <MenuItem
            disabled={actionInProgress}
            onClick={() => {
              const c = actionMenuClaim;
              handleCloseActionMenu();
              openDenyDialog(c);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <DenyIcon fontSize="small" sx={{ color: "#DC2626" }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500, color: "#DC2626" }}
            >
              Reject Bill
            </ListItemText>
          </MenuItem>
        )}

        {actionMenuClaim && canDelete(actionMenuClaim) && <Divider sx={{ my: 0.5 }} />}

        {actionMenuClaim && canDelete(actionMenuClaim) && (
          <MenuItem
            disabled={actionInProgress}
            onClick={() => {
              const c = actionMenuClaim;
              handleCloseActionMenu();
              setDeleteClaim(c);
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <DeleteIcon fontSize="small" sx={{ color: "#DC2626" }} />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500, color: "#DC2626" }}
            >
              Delete Draft
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete dialog */}
      <Dialog open={Boolean(deleteClaim)} onClose={() => setDeleteClaim(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete draft {deleteClaim?.claimNumber}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This removes the draft claim from the list.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteClaim(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Claim Dialog */}
      <Dialog
        open={Boolean(emailClaim)}
        onClose={handleCloseEmailDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EmailIcon sx={{ color: "#48CAE4", fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Claim {emailClaim?.claimNumber ? `• ${emailClaim.claimNumber}` : ""}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleCloseEmailDialog} disabled={sendingEmail}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          {/* Claim summary banner */}
          <Box
            sx={{
              p: 1.75,
              mb: 2.5,
              borderRadius: "12px",
              backgroundColor: "rgba(72, 202, 228, 0.08)",
              border: "1px solid rgba(72, 202, 228, 0.25)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                {emailClaim?.residentName || "Resident"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Form: {emailClaim?.formType || "CMS-1500"} • Payer: {emailClaim?.payerName || "—"}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0284C7" }}>
                {emailClaim ? formatCurrency(emailClaim.billedAmount) : "$0.00"}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Billed Amount
              </Typography>
            </Box>
          </Box>

          {loadingInsurances ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4, gap: 1.5 }}>
              <CircularProgress size={24} sx={{ color: "#48CAE4" }} />
              <Typography variant="body2" color="text.secondary">
                Loading resident insurance details...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
              {/* Insurance / Payer: Static / Read-only (Claimed Payer name directly, no dropdown) */}
              <TextField
                fullWidth
                size="small"
                label="Insurance / Payer"
                value={emailClaim?.payerName || "—"}
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    fontWeight: 600,
                    color: "text.primary",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#F8FAFC",
                  },
                }}
              />

              {/* Recipient Email: Multi-Select Dropdown with Checkboxes */}
              <Autocomplete
                multiple
                freeSolo
                disableCloseOnSelect
                options={Array.from(new Set([...availableEmailOptions.map((o) => o.email), ...selectedEmails]))}
                value={selectedEmails}
                inputValue={emailInputValue}
                onInputChange={(_, newInputValue) => setEmailInputValue(newInputValue)}
                onChange={(_, newValue) => {
                  const cleaned = Array.from(
                    new Set(
                      newValue
                        .map((v) => (typeof v === "string" ? v.trim() : ""))
                        .filter(Boolean)
                    )
                  );
                  setSelectedEmails(cleaned);
                }}
                renderOption={(props, option, { selected }) => {
                  const meta = availableEmailOptions.find(
                    (o) => o.email.toLowerCase() === option.toLowerCase()
                  );
                  return (
                    <li {...props} key={option}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        sx={{
                          color: "#64748B",
                          "&.Mui-checked": { color: "#0F172A" },
                        }}
                      />
                      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1, overflow: "hidden" }}>
                        <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 500, color: "#0F172A" }}>
                          {option}
                        </Typography>
                        {meta?.label && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: meta.isClaimPayer ? "#1E293B" : "#64748B",
                              fontWeight: meta.isClaimPayer ? 600 : 400,
                            }}
                          >
                            {meta.label}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        bgcolor: "#F1F5F9",
                        color: "#0F172A",
                        border: "1px solid #CBD5E1",
                        fontWeight: 600,
                        "& .MuiChip-deleteIcon": {
                          color: "#475569",
                          "&:hover": { color: "#0F172A" },
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    required
                    label="Recipient Email(s)"
                    placeholder={selectedEmails.length === 0 ? "Select or enter emails..." : "Add more emails..."}
                    error={selectedEmails.length === 0 && !emailInputValue.trim()}
                    sx={{
                      "& .MuiInputLabel-root.Mui-focused": { color: "#0F172A" },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#0F172A",
                      },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        const parts = emailInputValue.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
                        if (parts.length > 0) {
                          setSelectedEmails((prev) => Array.from(new Set([...prev, ...parts])));
                          setEmailInputValue("");
                        }
                      }
                    }}
                    onBlur={() => {
                      const parts = emailInputValue.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
                      if (parts.length > 0) {
                        setSelectedEmails((prev) => Array.from(new Set([...prev, ...parts])));
                        setEmailInputValue("");
                      }
                    }}
                  />
                )}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Button onClick={handleCloseEmailDialog} color="inherit" disabled={sendingEmail} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={sendingEmail ? <CircularProgress size={16} color="inherit" /> : <SubmitIcon />}
            disabled={sendingEmail || (selectedEmails.length === 0 && !emailInputValue.trim()) || loadingInsurances}
            onClick={handleSendEmail}
            sx={{
              ...actionButtonSx,
              "&.Mui-disabled": {
                bgcolor: "#E2E8F0",
                color: "#94A3B8",
              },
            }}
          >
            {sendingEmail ? "Sending..." : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "auto",
            maxWidth: 420,
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.15)",
            borderRadius: "10px",
            fontWeight: 600,
            fontSize: "0.875rem",
            alignItems: "center",
            ...(snackbar.severity === "success" && {
              bgcolor: "#F0FDF4 !important",
              color: "#166534",
              border: "1px solid #86EFAC",
              "& .MuiAlert-icon": { color: "#16A34A" },
            }),
            ...(snackbar.severity === "error" && {
              bgcolor: "#FEF2F2 !important",
              color: "#991B1B",
              border: "1px solid #FCA5A5",
              "& .MuiAlert-icon": { color: "#DC2626" },
            }),
            ...(snackbar.severity === "warning" && {
              bgcolor: "#FFFBEB !important",
              color: "#92400E",
              border: "1px solid #FDE68A",
              "& .MuiAlert-icon": { color: "#D97706" },
            }),
            ...(snackbar.severity === "info" && {
              bgcolor: "#EFF6FF !important",
              color: "#1E40AF",
              border: "1px solid #BFDBFE",
              "& .MuiAlert-icon": { color: "#2563EB" },
            }),
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillingClaimsPage;
